import React, { Component } from 'react';
import { cloneDeep, isEqual } from 'lodash';

import { api } from '../../../services/api';
import analyticService from '../../../services/analytics';
import { Loader } from '../../misc/loader-utils';
import { getClass } from '../../../utils/component-helpers';

import { ReactComponent as PlusIcon } from '../../../images/icons/icon-plus-inactive.svg';

import './article-search-bar.scss';

class ArticleSearchBar extends Component {
  constructor(props) {
    super(props);

    this.orgId = this.props.orgId;
    this.resultsNode = React.createRef();
    this.filterList = [
      // TODO: Initial release only includes brand search
      // {label: 'All', type: 'all'},
      { label: 'Keyword', type: 'keyword', examples: 'best smelling shampoos, 5 Cool Pools' },
      { label: 'Product', type: 'product', examples: 'Acne Treatment Gel, NuFACE Trinity' },
      { label: 'Brand', type: 'brand', examples: 'Adidas, SkinCeuticals' },
    ];

    this.resultsContainer = React.createRef();
    this.selectedResult = React.createRef();

    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.handleKeyPress = this.handleKeyPress.bind(this);

    this.state = {
      search: '',
      results: [],
      showTypeAhead: false,
      loading: false,
      selectedIdx: 0,
      chips: [],
      showModal: false,
      newFilter: null,
    };
  }

  componentDidMount() {
    window.addEventListener('click', this.handleClickOutside);
    window.addEventListener('keydown', this.handleKeyPress);
  }

  componentWillUnmount() {
    window.removeEventListener('click', this.handleClickOutside);
    window.removeEventListener('keydown', this.handleKeyPress);
  }

  componentDidUpdate(prevProps, _) {
    if (!isEqual(prevProps.filter, this.props.filter)) {
      this.queryTypeAhead();
    }
  }

  handleResultClick(search) {
    if (this.state.chips.includes(search) || this.state.chips.length >= 10) return;

    this.setState(
      {
        chips: this.state.chips.concat(search),
      },
      () => {
        analyticService.trackEvent(
          'Article Search',
          'Click',
          `${search} chip added for ${this.props.filter},${this.state.search} search`,
        );
      },
    );
  }

  deleteChip(value) {
    this.setState(
      {
        chips: this.state.chips.filter(chip => chip !== value),
      },
      () => {
        analyticService.trackEvent(
          'Article Search',
          'Click',
          `${value} chip removed from ${this.props.filter},${this.state.search} search`,
        );
      },
    );
  }

  clearChips() {
    this.setState(
      {
        chips: [],
      },
      () => {
        analyticService.trackEvent(
          'Article Search',
          'Click',
          `Chips removed for ${this.props.filter},${this.state.search} search`,
        );
      },
    );
  }

  handleKeyPress(e) {
    if (!this.state.showTypeAhead || ![38, 40].includes(e.keyCode) || !this.state.results) return;

    const index = this.state.selectedIdx;
    const resultHeight = this.selectedResult.current.getBoundingClientRect().height;
    const { scrollTop } = this.resultsContainer.current;
    if (e.keyCode === 38 && index > 0) {
      // Scroll Up
      this.setState({ selectedIdx: index - 1 }, () => {
        if (resultHeight * (index - 1) < scrollTop) {
          this.resultsContainer.current.scrollTop -= resultHeight;
        }
      });
    } else if (e.keyCode === 40 && index < this.state.results.length - 1) {
      // Scroll down
      this.setState({ selectedIdx: index + 1 }, () => {
        if (resultHeight * (index + 1) >= scrollTop + resultHeight * 10) {
          this.resultsContainer.current.scrollTop += resultHeight;
        }
      });
    }
  }

  handleClickOutside(e) {
    if (
      this.state.showTypeAhead &&
      (!this.resultsNode || !this.resultsNode.current.contains(e.target))
    ) {
      this.setState({ showTypeAhead: false });
    }

    if (
      !this.state.showTypeAhead &&
      this.state.search &&
      this.resultsNode &&
      this.resultsNode.current.contains(e.target)
    ) {
      this.setState({
        showTypeAhead: true,
        selectedIdx: 0,
      });
    }
  }

  handleFormChange(e) {
    this.setState(
      {
        search: e.target.value,
        showTypeAhead: !!e.target.value,
        loading: true,
      },
      () => {
        this.debounceTypeAhead();
      },
    );
  }

  handleFormSubmission(e) {
    e.preventDefault();
    const search = this.state.results[this.state.selectedIdx].term;

    if (this.state.chips.includes(search)) return;

    this.setState(
      {
        chips: this.state.chips.concat(search),
      },
      () => {
        analyticService.trackEvent(
          'Article Search',
          'Click',
          `${search} chip added for ${this.props.filter},${this.state.search} search`,
        );
      },
    );
  }

  debounceTypeAhead() {
    if (this.timeout) {
      clearTimeout(this.timeout);
    }

    this.timeout = setTimeout(() => {
      this.queryTypeAhead();
    }, 1000);
  }

  async queryTypeAhead() {
    if (!this.state.search) {
      this.setState({
        results: [],
        loading: false,
        selectedIdx: 0,
      });
      return;
    }

    const params = {
      search_query: this.state.search,
    };

    if (this.props.filter !== 'all') {
      params.type = this.props.filter;
    }

    analyticService.trackEvent(
      'Article Search',
      'typeAhead',
      `${this.props.filter} typeAhead for ${this.state.search}`,
    );

    api
      .get(`/api/v0/merchants/${this.orgId}/article_search/type_ahead/`, params)
      .then(data => {
        this.setState({
          results: data.data.data[0].stats,
          loading: false,
          selectedIdx: 0,
        });
      })
      .catch(error => {
        this.setState({
          results: null,
          loading: false,
          selectedIdx: 0,
        });
      });
  }

  searchResults(singleResult) {
    if (!this.state.chips && !singleResult) return;

    const result = this.state.results[this.state.selectedIdx].term;
    this.setState(
      {
        showTypeAhead: false,
        chips: singleResult ? [result] : this.state.chips,
        search: '',
      },
      () => {
        analyticService.trackEvent(
          'Article Search',
          'Click',
          `Search for ${this.props.filter},${this.state.chips}`,
        );
        this.props.update(this.state.chips);
      },
    );
  }

  updateFilter(filter) {
    if (filter.type === this.props.filter) return;

    if (this.state.chips.length) {
      this.setState({
        showModal: true,
        newFilter: filter.type,
      });
    } else {
      this.props.updateFilter(filter.type);
    }
  }

  handleModal(confirmation) {
    if (!this.state.showModal) return;

    const { newFilter } = this.state;
    this.setState(
      {
        showModal: false,
        newFilter: null,
        chips: confirmation ? [] : this.state.chips,
      },
      () => {
        if (confirmation) this.props.updateFilter(newFilter);
      },
    );
  }

  render() {
    const filters = this.filterList.map((filter, idx) => {
      const selected = filter.type === this.props.filter;
      return (
        <button
          className={`filter-button ${filter.type} ${selected ? 'selected' : ''}`}
          onClick={() => this.updateFilter(filter)}
          key={idx}
        >
          {filter.label}
        </button>
      );
    });

    const seeResultsLink = !this.state.chips.length ? (
      <a className="see-results-button" onClick={() => this.searchResults(true)}>
        See Results
      </a>
    ) : null;

    const containerHeight = this.state.results.length
      ? this.state.results.length > 10
        ? 500 // full-sized result container
        : this.state.results.length * 48 + 2 // each result is 48px height + 2px border
      : 92; // No results container is 92px height

    const typeAhead =
      !this.state.loading && this.state.results && this.state.showTypeAhead
        ? this.state.results.map((result, idx) => {
            const type = this.filterList.find(filter => filter.type === result.type).label;
            return (
              <div
                className={`type-ahead-result ${idx === this.state.selectedIdx ? 'selected' : ''}`}
                ref={idx === this.state.selectedIdx ? this.selectedResult : ''}
                key={idx}
                onClick={() => this.handleResultClick(result.term)}
                onMouseOver={() => this.setState({ selectedIdx: idx })}
                onFocus={() => this.setState({ selectedIdx: idx })}
              >
                <p className="type">{type}</p>
                <p className="value">{result.term}</p>
                <p className="count">{result.count} articles</p>
                {idx === this.state.selectedIdx ? <PlusIcon className="plus-icon" /> : null}
                {idx === this.state.selectedIdx ? seeResultsLink : null}
              </div>
            );
          })
        : null;

    const emptyTypeAhead =
      !this.state.loading && this.state.showTypeAhead ? (
        <div className="empty-type-ahead">
          <p>
            0 results for "<span>{this.state.search}</span>"
          </p>
          <p>Try searching for another keyword, product, or brand.</p>
        </div>
      ) : null;

    const header = !this.props.showTable ? <h1>Article Search</h1> : null;

    const selectedFilter = this.filterList.find(filter => filter.type === this.props.filter);
    const examples = !this.props.showTable ? (
      <div className="example-search-container">
        <p>Example {selectedFilter.type} searches:</p>
        <p className="examples">{selectedFilter.examples}</p>
      </div>
    ) : null;

    const chips = this.state.chips.map((chip, idx) => {
      return (
        <div className="chip" key={idx}>
          <p>{chip}</p>
          <div className="delete-chip" onClick={() => this.deleteChip(chip)} />
        </div>
      );
    });

    const maxSelected =
      this.state.chips.length >= 10 ? <p className="max-chips">(Max 10 selected)</p> : null;

    const placeHolder =
      this.props.showTable && this.state.chips.length
        ? ''
        : `Start typing to search by ${this.props.filter}...`;

    const confirmationModal = (
      <div className={`confirmation-modal ${this.state.showModal ? 'active' : ''}`}>
        <div className="modal-container">
          <div className="confirmation-text-container">
            <p className="confirmation-header">Clear Search?</p>
            <p className="confirmation-text">Changing tabs will clear your search.</p>
          </div>
          <div className="confirmation-button-container">
            <button className="cancel-btn" onClick={() => this.handleModal(false)}>
              Cancel
            </button>
            <button className="confirm-btn" onClick={() => this.handleModal(true)}>
              Clear
            </button>
          </div>
        </div>
      </div>
    );

    return (
      <div className={`article-search-bar ${this.props.showTable ? 'minimized' : ''}`}>
        {header}
        <div className="filter-container">
          <p>Search by:</p>
          {filters}
        </div>
        <form
          className="search-container"
          onSubmit={e => this.handleFormSubmission(e)}
          ref={this.resultsNode}
        >
          <div className={`search-input-container ${this.state.showTypeAhead ? 'opened' : ''}`}>
            <div className="narrativ-icon search" />
            <div className="search-bar-container">
              <div className="search-bar-chips">
                {chips}
                {maxSelected}
                <input
                  value={this.state.search}
                  onChange={e => this.handleFormChange(e)}
                  placeholder={placeHolder}
                  className="search-bar"
                />
              </div>
            </div>
            <div className={`search-button-container ${this.state.chips.length ? 'active' : ''}`}>
              <span className="clear-search-button" onClick={() => this.clearChips()}>
                Clear
              </span>
              <div className="search-button" onClick={() => this.searchResults()}>
                <span>Search</span>
              </div>
            </div>
          </div>
          <div
            className={getClass('search-results-container', {
              opened: this.state.showTypeAhead,
              loading: this.state.loading,
            })}
            style={{ '--search-height': `-${containerHeight}px` }}
            ref={this.resultsContainer}
          >
            <Loader loading={this.state.loading} />
            {this.state.results.length ? typeAhead : emptyTypeAhead}
          </div>
        </form>
        {examples}
        {confirmationModal}
      </div>
    );
  }
}

export default ArticleSearchBar;
